'use client';

import { usePathname } from 'next/navigation';
import { type RefObject, useCallback, useEffect } from 'react';
type UseIframeCleanupOptions = {
  /**
   * Return true when the iframe is allowed to remain active for the current pathname.
   * If this returns false, the iframe will be blanked.
   */
  isActivePath?: (pathname: string) => boolean;
  /** Optional extra cleanup (e.g. pause a player.js instance) before blanking src. */
  beforeBlank?: () => void;
};
export function blankIframe(iframe: HTMLIFrameElement) {
  if (iframe.dataset.playbackSuspended === 'true' || !iframe.contentWindow) return;
  const currentSrc = iframe.getAttribute('src') ?? iframe.src;
  if (currentSrc && currentSrc !== 'about:blank') {
    iframe.dataset.originalSrc = currentSrc;
  }
  // Iframe navigations share the tab's history. Cleanup must not consume Back.
  iframe.contentWindow.location.replace('about:blank');
  iframe.dataset.playbackSuspended = 'true';
}
export function restoreIframe(iframe: HTMLIFrameElement) {
  if (!iframe.contentWindow) return;
  const currentSrc = iframe.getAttribute('src');
  const hasSource = Boolean(currentSrc && currentSrc !== 'about:blank');
  if (iframe.dataset.playbackSuspended !== 'true' && hasSource) return;
  const originalSrc = hasSource ? currentSrc : iframe.dataset.originalSrc;
  if (!originalSrc) return;
  iframe.contentWindow.location.replace(originalSrc);
  delete iframe.dataset.playbackSuspended;
}

/**
 * Self-cleaning iframe helper for SPA navigation. It does not rely on React unmount
 * happening promptly (App Router caches can keep trees mounted), and instead blanks
 * the iframe src when the route is no longer active.
 */
export function useIframeCleanup(iframeRef: RefObject<HTMLIFrameElement | null>, options: UseIframeCleanupOptions = {}) {
  const pathname = usePathname();
  const {
    isActivePath,
    beforeBlank
  } = options;
  const shouldKeepActive = useCallback(() => {
    if (!isActivePath) return true;
    return isActivePath(pathname);
  }, [isActivePath, pathname]);
  const kill = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      beforeBlank?.();
    } catch {}
    blankIframe(iframe);
  }, [beforeBlank, iframeRef]);
  useEffect(() => {
    return () => {
      kill();
    };
  }, [kill]);
  useEffect(() => {
    if (!shouldKeepActive()) {
      kill();
    }
  }, [pathname, kill, shouldKeepActive]);
  useEffect(() => {
    if (!shouldKeepActive()) return;
    const iframe_0 = iframeRef.current;
    if (!iframe_0) return;
    restoreIframe(iframe_0);
  }, [pathname, shouldKeepActive, iframeRef]);
  useEffect(() => {
    const handlePopState = () => {
      if (!shouldKeepActive()) {
        kill();
        return;
      }
      const iframe_1 = iframeRef.current;
      if (iframe_1) restoreIframe(iframe_1);
    };
    const handlePageHide = () => {
      kill();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        if (!shouldKeepActive()) kill();
      }
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [kill, shouldKeepActive]);
}